import { useEffect, useState, useMemo } from "react";
import api from "../lib/api";
import { fmtTRY } from "../lib/format";
import { PageHeader } from "../components/Common";
import { DownloadSimple, MagnifyingGlass, CaretUp, CaretDown, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from "recharts";

const nf = (v) => new Intl.NumberFormat("tr-TR", { notation: "compact", maximumFractionDigits: 1 }).format(v);
const roiOf = (r) => (r.purchase > 0 ? (r.profit / r.purchase) * 100 : null);

export default function VehicleProfitability() {
  const [rows, setRows] = useState([]);
  const [range, setRange] = useState({ start: "", end: "" });
  const [search, setSearch] = useState("");
  const [soldOnly, setSoldOnly] = useState(false);
  const [sort, setSort] = useState({ key: "profit", dir: "desc" });

  useEffect(() => {
    const p = new URLSearchParams();
    if (range.start) p.set("start", range.start);
    if (range.end) p.set("end", range.end);
    const q = p.toString();
    api.get(`/vehicle-profitability${q ? `?${q}` : ""}`).then(({ data }) => setRows(data));
  }, [range]);

  const download = async (fmt) => {
    const p = new URLSearchParams();
    if (range.start) p.set("start", range.start);
    if (range.end) p.set("end", range.end);
    const q = p.toString();
    try {
      const { data } = await api.get(`/reports/profitability-${fmt}${q ? `?${q}` : ""}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `filofinans-arac-karliligi.${fmt === "excel" ? "xlsx" : "pdf"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("İndirme başarısız"); }
  };

  const toggleSort = (key) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === "desc" ? "asc" : "desc" } : { key, dir: "desc" }));
  };

  const view = useMemo(() => {
    let out = rows;
    if (soldOnly) out = out.filter((r) => r.status === "sold");
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      out = out.filter((r) => r.plate.toLowerCase().includes(q) || (r.vehicle || "").toLowerCase().includes(q));
    }
    const key = sort.key;
    const dir = sort.dir === "desc" ? -1 : 1;
    out = [...out].sort((a, b) => {
      const av = key === "roi" ? (roiOf(a) ?? -Infinity) : a[key];
      const bv = key === "roi" ? (roiOf(b) ?? -Infinity) : b[key];
      return (av > bv ? 1 : av < bv ? -1 : 0) * dir;
    });
    return out;
  }, [rows, soldOnly, search, sort]);

  const chartData = view.map((r) => ({ name: r.plate, kar: r.profit }));
  const totals = view.reduce((a, r) => ({ income: a.income + r.income, expense: a.expense + r.expense, profit: a.profit + r.profit, purchase: a.purchase + r.purchase }), { income: 0, expense: 0, profit: 0, purchase: 0 });
  const roi = totals.purchase > 0 ? (totals.profit / totals.purchase) * 100 : null;

  return (
    <div className="p-8 max-w-[1400px]">
      <PageHeader title="Araç Kârlılığı" subtitle="Araç bazlı gerçek kâr/zarar: operasyonel gelir/gider + alım bedeli + kredi faizi ve satış geliri dahil" action={
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <div className="flex items-center gap-1.5 border border-zinc-200 rounded-sm px-2 py-1" data-testid="profit-date-filter">
            <input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} data-testid="profit-start" className="text-xs font-mono text-zinc-700 outline-none bg-transparent" title="İşlem tarihi başlangıç" />
            <span className="text-zinc-300 text-xs">–</span>
            <input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} data-testid="profit-end" className="text-xs font-mono text-zinc-700 outline-none bg-transparent" title="İşlem tarihi bitiş" />
            {(range.start || range.end) && <button onClick={() => setRange({ start: "", end: "" })} data-testid="profit-clear-filter" className="text-zinc-400 hover:text-zinc-900"><X size={13} /></button>}
          </div>
          <button onClick={() => download("excel")} data-testid="profit-excel-button" className="flex items-center gap-1.5 border border-zinc-200 rounded-sm px-3 py-2.5 text-sm font-semibold text-zinc-700 hover:bg-zinc-100 transition-colors"><DownloadSimple size={15} weight="bold" className="text-green-600" />Excel</button>
          <button onClick={() => download("pdf")} data-testid="profit-pdf-button" className="flex items-center gap-1.5 border border-zinc-200 rounded-sm px-3 py-2.5 text-sm font-semibold text-zinc-700 hover:bg-zinc-100 transition-colors"><DownloadSimple size={15} weight="bold" className="text-red-600" />PDF</button>
        </div>
      } />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4 animate-fade-up">
        <PCard label="Toplam Gelir" value={fmtTRY(totals.income)} cls="text-green-600" />
        <PCard label="Toplam Gider" value={fmtTRY(totals.expense)} cls="text-red-600" />
        <PCard label="Net Kâr / Zarar" value={fmtTRY(totals.profit)} cls={totals.profit >= 0 ? "text-zinc-950" : "text-red-600"} />
        <PCard label="Yatırım Getirisi (ROI)" value={roi === null ? "—" : `%${roi.toFixed(1)}`} cls={roi !== null && roi < 0 ? "text-red-600" : "text-[#002FA7]"} sub={totals.purchase > 0 ? `Toplam alım: ${fmtTRY(totals.purchase)}` : "Alım bedeli girilmemiş"} />
      </div>

      {view.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-sm p-6 mb-4 animate-fade-up" data-testid="profitability-chart">
          <h3 className="font-display text-lg font-semibold text-zinc-900 mb-4">Araç Bazında Net Kâr</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} margin={{ left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f4" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#71717a", fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={nf} tick={{ fontSize: 11, fill: "#a1a1aa", fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v) => fmtTRY(v)} contentStyle={{ borderRadius: 4, border: "1px solid #e4e4e7", fontSize: 12 }} />
              <Bar dataKey="kar" name="Net Kâr" radius={[2, 2, 0, 0]}>
                {chartData.map((e, i) => <Cell key={e.name || i} fill={e.kar >= 0 ? "#16A34A" : "#dc2626"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2 border border-zinc-200 rounded-sm px-3 py-2 bg-white flex-1 min-w-[200px] max-w-sm">
          <MagnifyingGlass size={16} className="text-zinc-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="profit-search" placeholder="Plaka veya araç ara..." className="text-sm outline-none bg-transparent w-full" />
          {search && <button onClick={() => setSearch("")} className="text-zinc-400 hover:text-zinc-900"><X size={14} /></button>}
        </div>
        <label className="flex items-center gap-2 border border-zinc-200 rounded-sm px-3 py-2 bg-white cursor-pointer text-sm font-medium text-zinc-700">
          <input type="checkbox" checked={soldOnly} onChange={(e) => setSoldOnly(e.target.checked)} data-testid="profit-sold-only" className="w-4 h-4 accent-[#002FA7]" />
          Yalnızca satılanlar
        </label>
        <span className="text-xs text-zinc-400 font-mono">{view.length} araç</span>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden animate-fade-up" data-testid="profitability-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50">
              <th className="text-left font-semibold text-zinc-600 px-3 py-3 text-xs uppercase tracking-wider">#</th>
              <th className="text-left font-semibold text-zinc-600 px-3 py-3 text-xs uppercase tracking-wider">Plaka</th>
              <th className="text-left font-semibold text-zinc-600 px-3 py-3 text-xs uppercase tracking-wider">Araç</th>
              <SortTh label="Op. Gelir" k="op_income" sort={sort} onSort={toggleSort} />
              <SortTh label="Op. Gider" k="op_expense" sort={sort} onSort={toggleSort} />
              <SortTh label="Faiz" k="finance_cost" sort={sort} onSort={toggleSort} />
              <SortTh label="Alım Bedeli" k="purchase" sort={sort} onSort={toggleSort} />
              <SortTh label="Satış Geliri" k="sale" sort={sort} onSort={toggleSort} />
              <SortTh label="Net Kâr/Zarar" k="profit" sort={sort} onSort={toggleSort} />
              <SortTh label="ROI" k="roi" sort={sort} onSort={toggleSort} />
            </tr>
          </thead>
          <tbody>
            {view.map((r, i) => {
              const rv = roiOf(r);
              return (
                <tr key={r.plate} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors" data-testid={`profit-row-${r.plate}`}>
                  <td className="px-3 py-3 font-mono text-zinc-400">{i + 1}</td>
                  <td className="px-3 py-3 font-mono font-semibold text-zinc-900">{r.plate}{r.status === "sold" && <span className="ml-2 text-[10px] uppercase tracking-wider font-semibold text-zinc-500 bg-zinc-200 rounded-sm px-1.5 py-0.5">Satıldı</span>}</td>
                  <td className="px-3 py-3 text-zinc-600">{r.vehicle}</td>
                  <td className="px-3 py-3 text-right font-mono text-green-600 tabular-nums">{fmtTRY(r.op_income)}</td>
                  <td className="px-3 py-3 text-right font-mono text-red-600 tabular-nums">{fmtTRY(r.op_expense)}</td>
                  <td className="px-3 py-3 text-right font-mono text-orange-600 tabular-nums">{r.finance_cost ? `-${fmtTRY(r.finance_cost)}` : "—"}</td>
                  <td className="px-3 py-3 text-right font-mono text-zinc-500 tabular-nums">{r.purchase ? `-${fmtTRY(r.purchase)}` : "—"}</td>
                  <td className="px-3 py-3 text-right font-mono text-zinc-700 tabular-nums">{r.sale ? fmtTRY(r.sale) : "—"}</td>
                  <td className={`px-3 py-3 text-right font-mono font-semibold tabular-nums ${r.profit >= 0 ? "text-zinc-900" : "text-red-600"}`}>{fmtTRY(r.profit)}</td>
                  <td className={`px-3 py-3 text-right font-mono tabular-nums ${rv !== null ? (r.profit >= 0 ? "text-[#002FA7]" : "text-red-600") : "text-zinc-300"}`}>{rv !== null ? `%${rv.toFixed(1)}` : "—"}</td>
                </tr>
              );
            })}
            {view.length === 0 && (
              <tr><td colSpan={10} className="px-4 py-12 text-center text-zinc-400">Sonuç bulunamadı.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SortTh({ label, k, sort, onSort }) {
  const active = sort.key === k;
  return (
    <th className="px-3 py-3 text-xs uppercase tracking-wider" data-testid={`sort-${k}`}>
      <button onClick={() => onSort(k)} className={`flex items-center gap-1 ml-auto font-semibold ${active ? "text-[#002FA7]" : "text-zinc-600"} hover:text-zinc-900`}>
        {label}
        {active ? (sort.dir === "desc" ? <CaretDown size={12} weight="bold" /> : <CaretUp size={12} weight="bold" />) : <CaretDown size={12} className="text-zinc-300" />}
      </button>
    </th>
  );
}

function PCard({ label, value, cls, sub }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-5">
      <span className="text-xs uppercase tracking-[0.15em] font-semibold text-zinc-500">{label}</span>
      <div className={`font-mono text-2xl font-bold mt-2 tabular-nums ${cls || "text-zinc-950"}`}>{value}</div>
      {sub && <div className="text-[11px] text-zinc-400 mt-1">{sub}</div>}
    </div>
  );
}
